import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { proofPhotoKey, recallEvent, rememberEvent, stopPaymentKey } from "./eventMemory";
import {
  getIsSocketConnected,
  subscribeAgent,
  subscribeConnection,
  subscribeOrder,
  subscribeTrip,
  type AgentLocationUpdatedPayload,
  isRoomJoined,
  subscribeResync,
  subscribeRoomStatus,
  subscribeStopPayments,
  type OrderUpdatedPayload,
  type StopPaymentDecision,
  type StopPaymentPayload,
  type TripAwaitingApprovalPayload,
} from "./socketClient";

// Souscrit aux updates temps réel d'une commande et invalide la query React Query
// associée pour déclencher un re-fetch (source de vérité = backend).
// Retourne aussi le dernier payload reçu pour des updates instantanés UI.

export function useOrderLiveUpdates(orderId: string | undefined) {
  const qc = useQueryClient();
  const [last, setLast] = useState<OrderUpdatedPayload | null>(null);

  useEffect(() => {
    if (!orderId) return;
    const unsub = subscribeOrder(orderId, (payload) => {
      setLast(payload);
      // Invalide le détail → re-fetch (utile pour récupérer items + agent_id)
      qc.invalidateQueries({ queryKey: ["orders", "detail", orderId] });
      // Invalide aussi la liste si l'écran courant l'utilise
      qc.invalidateQueries({ queryKey: ["orders", "list"] });
    });
    return unsub;
  }, [orderId, qc]);

  return last;
}

/**
 * Position GPS d'une Main via SA salle (`agent:{id}`).
 *
 * ⚠️ R7 — Pour suivre une COURSE, préférez `useTripLiveUpdates` : la salle
 * `trip:{id}` porte désormais la position, et le client s'y trouve déjà. Ce
 * hook ne reste nécessaire que pour le suivi d'une COMMANDE, qui n'a pas de
 * salle de course où se rattacher.
 *
 * `enabled` applique R9 : « vous restez dans la salle après la fin de la course,
 * et vous continuez à recevoir la position de cette personne, sur ses autres
 * missions ». Passer `false` sur un état terminal quitte la salle — sans quoi
 * un client qui laisse l'onglet ouvert suit un livreur qui ne travaille plus
 * pour lui.
 */
export function useAgentLiveLocation(agentId: string | undefined, enabled = true) {
  const [last, setLast] = useState<AgentLocationUpdatedPayload | null>(null);

  useEffect(() => {
    if (!agentId || !enabled) return;
    const unsub = subscribeAgent(agentId, setLast);
    return unsub;
  }, [agentId, enabled]);

  return last;
}

/**
 * Temps réel d'une COURSE — le chemin normal du suivi (R7/R8/R11).
 *
 * Une seule salle, `trip:{id}`, où TOUT arrive : changements d'état, offres,
 * expirations, abandons, demandes de validation, et la position de la Main.
 *
 * ## Ce que ce hook remplace
 * L'app interrogeait `GET /trips/{id}` toutes les vingt secondes pendant que le
 * canal temps réel restait vide. Ça fonctionne, au prix d'une charge inutile et
 * d'une carte figée — le sondage ne rendra jamais une position au rythme où
 * elle arrive. Ici, chaque événement invalide la course : l'écran se met à jour
 * au moment où la chose se produit, et le sondage n'est plus qu'un filet quand
 * la connexion tombe.
 *
 * ## Pourquoi invalider plutôt qu'appliquer le payload
 * Le serveur reste la source de vérité. Un événement dit QUE quelque chose a
 * changé ; c'est `GET /trips/{id}` qui dit QUOI, avec ses arrêts, ses articles
 * et ses montants. Recomposer la course depuis des événements partiels
 * ferait diverger l'écran de la réalité au premier événement manqué.
 *
 * La POSITION fait exception : elle est rendue telle quelle, sans passer par le
 * cache — elle n'est portée par aucune requête REST, et un aller-retour par
 * arrêt GPS n'aurait aucun sens.
 */
export function useTripLiveUpdates(
  tripId: string | undefined,
  handlers?: {
    onAwaitingApproval?: (payload: TripAwaitingApprovalPayload) => void;
  },
): AgentLocationUpdatedPayload | null {
  const qc = useQueryClient();
  const [location, setLocation] = useState<AgentLocationUpdatedPayload | null>(null);

  // Les gestionnaires changent d'identité à chaque rendu de l'écran appelant :
  // les lire via une ref évite de quitter puis rejoindre la salle à chaque
  // frappe au clavier dans un champ voisin.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!tripId) return;
    return subscribeTrip(tripId, {
      onAgentLocation: setLocation,
      onAwaitingApproval: (payload) => {
        // ⚠️ RETENU ICI, et non dans l'écran qui l'affiche.
        //
        // Le `photo_media_id` du colis n'existe que dans cet événement — aucune
        // route REST ne le rend. Le mémoriser au niveau de l'ÉCRAN laissait
        // passer un cas entier : un client présent dans la salle de course mais
        // sur un autre écran (la carte plein écran, par exemple) reçoit bien
        // l'événement, mais sans gestionnaire `onAwaitingApproval` la charge
        // utile était jetée. Il revenait ensuite au suivi, et la photo n'avait
        // jamais existé pour lui.
        //
        // Au niveau du hook, TOUT écran qui écoute la course la retient — même
        // celui qui n'a rien à en faire sur le moment.
        if (payload?.photo_media_id) {
          rememberEvent(
            proofPhotoKey(tripId, payload.stop_index ?? 0),
            payload.photo_media_id,
          );
        }
        handlersRef.current?.onAwaitingApproval?.(payload);
      },
      onAnyEvent: () => {
        // Clés écrites en dur, comme pour les commandes ci-dessus : `shared` ne
        // doit pas dépendre de `features`.
        void qc.invalidateQueries({ queryKey: ["trips", "detail", tripId] });
        void qc.invalidateQueries({ queryKey: ["trips"] });
      },
    });
  }, [tripId, qc]);

  return location;
}

// État de connexion du socket realtime. Permet aux écrans live (tracking,
// mission) d'afficher un banner offline ET de basculer en polling REST si
// la connexion ne revient pas (cf. useOrderTrackingWithFallback).
export function useRealtimeConnected(): boolean {
  const [connected, setConnected] = useState<boolean>(getIsSocketConnected());

  useEffect(() => {
    return subscribeConnection(setConnected);
  }, []);

  return connected;
}

// Combine WebSocket + polling REST de secours. Si le socket est déconnecté
// pendant plus de `gracePeriodMs`, on active un re-fetch périodique de la
// query ['orders', 'detail', orderId] tous les `pollIntervalMs`.
//
// Utilisation type sur un TrackingScreen :
//   const orderQuery = useOrderDetail(orderId);
//   useOrderLiveUpdates(orderId);
//   const isLive = useOrderTrackingWithFallback(orderId);
export function useOrderTrackingWithFallback(
  orderId:         string | undefined,
  gracePeriodMs:   number = 5_000,
  pollIntervalMs:  number = 10_000,
): { isLive: boolean; isFallbackPolling: boolean } {
  const connected = useRealtimeConnected();
  const qc        = useQueryClient();
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!orderId)   return;
    if (connected) {
      setPolling(false);
      return;
    }

    // Démarre le fallback polling APRÈS gracePeriodMs sans connexion — évite
    // de pinger le backend pour une micro-coupure de 200ms.
    const startTimer = setTimeout(() => setPolling(true), gracePeriodMs);
    return () => clearTimeout(startTimer);
  }, [orderId, connected, gracePeriodMs]);

  useEffect(() => {
    if (!polling || !orderId) return;
    const interval = setInterval(() => {
      qc.invalidateQueries({ queryKey: ["orders", "detail", orderId] });
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [polling, orderId, qc, pollIntervalMs]);

  return { isLive: connected, isFallbackPolling: polling };
}

/**
 * Recharge les données de l'app quand le socket a repris SANS rejeu de session.
 *
 * Le serveur signale via `ready { recovered }` s'il a pu restituer les
 * événements manqués. Quand il ne l'a pas fait — coupure trop longue, instance
 * redémarrée —, ces événements sont perdus DÉFINITIVEMENT : un statut de course
 * passé à « livrée » pendant la coupure ne sera jamais réémis, et l'écran
 * resterait indéfiniment sur l'ancien état.
 *
 * On invalide alors tout le cache : TanStack refetch ce que les écrans montés
 * observent réellement, rien de plus.
 *
 * Monté une seule fois, dans les shells — un abonnement par écran déclencherait
 * autant de rechargements simultanés que d'écrans ouverts.
 */
export function useRealtimeResync(): void {
  const qc = useQueryClient();

  useEffect(() => {
    return subscribeResync(() => {
      void qc.invalidateQueries();
    });
  }, [qc]);
}


/**
 * Versement à la boutique, pour la Main — §2 et §8 du contrat du 2026-08-31.
 *
 * ## Ce que la Main ne pouvait pas savoir
 * Après l'accord du client sur les prix relevés, elle ne peut acheter qu'une
 * fois l'argent versé au vendeur. Depuis le 2026-09-04, la plateforme décaisse
 * elle-même vers le numéro de la boutique — le repli manuel existe encore, mais
 * ce n'est plus le chemin normal. `released` attend la confirmation du
 * prestataire : nominalement trois secondes, parfois plus, et jamais en cas
 * d'échec.
 *
 * Rien à l'écran ne disait où en était ce moment. La Main attendait devant le
 * comptoir sans savoir si elle devait patienter, relancer, ou repartir.
 *
 * ## Ce que ce hook rend
 * Le dernier verdict reçu POUR CETTE COURSE, et rien d'autre. L'écran en tire
 * trois états lisibles : versement attendu, versé (on peut acheter), refusé
 * (avec le motif de l'opérateur, affiché tel quel).
 *
 * Conformément au §0, l'événement invalide aussi la course : c'est
 * `GET /trips/{id}` qui fait foi sur l'état, pas la charge utile.
 */
export type StopPaymentVerdict = {
  decision: StopPaymentDecision;
  payload:  StopPaymentPayload;
  /** Horodatage de réception, pour distinguer deux verdicts successifs. */
  at:       number;
};

export function useStopPaymentUpdates(tripId: string | undefined): StopPaymentVerdict | null {
  const qc = useQueryClient();

  // ⚠️ Le verdict est RELU au montage, pas remis à zéro.
  //
  // Ce hook a d'abord été écrit avec un simple `useState(null)`, et c'était un
  // défaut sérieux : au rechargement, le verdict disparaissait alors qu'aucune
  // route REST ne peut le redonner — `TripStop` ne porte aucun champ de
  // versement. Le bandeau retombait donc sur « Versement en attente », dérivé
  // du statut `PURCHASING`, qui vaut AVANT COMME APRÈS le versement.
  //
  // Les deux conséquences étaient graves, et opposées :
  //   • un « Versement effectué ✓ » redevenait « en attente » — l'écran
  //     affirmait un état FAUX, et la Main attendait un argent déjà versé ;
  //   • un « Versement refusé » disparaissait purement et simplement, remplacé
  //     par une invitation à patienter. Une Main qui rouvre son onglet après un
  //     refus ne l'apprenait jamais, ni son motif.
  //
  // Cf. `eventMemory` pour ce que ce palliatif ne peut pas faire.
  const [verdict, setVerdict] = useState<StopPaymentVerdict | null>(() =>
    tripId ? recallEvent<StopPaymentVerdict>(stopPaymentKey(tripId)) : null,
  );

  // Changer de course reprend le verdict de CELLE-CI — jamais celui de la
  // précédente, qui débloquerait un achat à tort.
  useEffect(() => {
    setVerdict(tripId ? recallEvent<StopPaymentVerdict>(stopPaymentKey(tripId)) : null);
  }, [tripId]);

  useEffect(() => {
    if (!tripId) return;
    return subscribeStopPayments((decision, payload) => {
      // Même précaution que sur la salle de course : Socket.IO ne cloisonne pas
      // côté client, et une Main peut porter plus d'une course au contrat.
      if (typeof payload.trip_id === "string" && payload.trip_id !== tripId) return;

      const next: StopPaymentVerdict = { decision, payload, at: Date.now() };
      setVerdict(next);
      // Retenu AVANT tout : c'est la seule copie qui survivra au rechargement.
      rememberEvent(stopPaymentKey(tripId), next);
      // « L'événement dit va regarder » : l'état de l'arrêt vient du serveur.
      void qc.invalidateQueries({ queryKey: ["trips", "detail", tripId] });
    });
  }, [tripId, qc]);

  return verdict;
}

/**
 * La salle d'une course est-elle RÉELLEMENT acquise ?
 *
 * ## Pourquoi `useRealtimeConnected()` ne suffisait pas
 * Une socket branchée ne garantit rien sur UNE course en particulier. Trois
 * situations laissent la connexion vivante et la salle muette :
 *
 *   • l'entrée a été REFUSÉE (`non_autorise`) — définitif, aucune nouvelle
 *     tentative ;
 *   • elle est en ATTENTE (`indisponible`) — on réessaie, avec un délai qui
 *     monte jusqu'à trente secondes ;
 *   • une RECONNEXION vient d'avoir lieu : la socket est neuve, et aucune salle
 *     n'est acquise tant que le serveur ne l'a pas reconfirmée.
 *
 * Dans ces trois cas, un écran qui coupe son sondage sur la seule foi de la
 * connexion se fige en silence. C'est ce qui faisait qu'une Main devant son
 * comptoir ne voyait la validation du client qu'après avoir rechargé la page :
 * plus d'événements, et plus de sondage non plus.
 */
export function useTripRoomJoined(tripId: string | undefined): boolean {
  const [joined, setJoined] = useState(() => isRoomJoined("trip", tripId));

  useEffect(() => {
    // Relire à chaque changement de course : l'état de la salle précédente ne
    // dit rien de la nouvelle.
    setJoined(isRoomJoined("trip", tripId));
    if (!tripId) return;

    return subscribeRoomStatus((key) => {
      if (key !== `trip:${tripId}`) return;
      setJoined(isRoomJoined("trip", tripId));
    });
  }, [tripId]);

  return joined;
}
